<template>
  <div class="target-duration-field">
    <el-select
      :model-value="selectedOption"
      placeholder="选择目标时长"
      @update:model-value="handleOptionChange"
    >
      <el-option
        v-for="option in presetOptions"
        :key="option.value"
        :label="option.label"
        :value="option.value"
      />
      <el-option label="自定义" :value="CUSTOM_OPTION" />
    </el-select>

    <div v-if="selectedOption === CUSTOM_OPTION" class="target-duration-field__custom">
      <el-input-number
        :model-value="customSeconds"
        :min="5"
        :max="3600"
        :step="5"
        :precision="0"
        controls-position="right"
        @update:model-value="handleCustomSecondsChange"
      />
      <span class="target-duration-field__suffix">秒</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

const CUSTOM_OPTION = 'custom'
const presetOptions = [
  { label: '30 秒', value: 0.5 },
  { label: '60 秒', value: 1 },
  { label: '90 秒', value: 1.5 },
  { label: '180 秒', value: 3 },
] as const

const props = defineProps<{
  modelValue: number
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: number): void
}>()

const selectedOption = ref<number | typeof CUSTOM_OPTION>(1)
const customSeconds = ref(60)

function isPresetValue(value: unknown) {
  return presetOptions.some((option) => option.value === Number(value))
}

function toRoundedSeconds(value: unknown) {
  const minutes = Number(value)
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return 60
  }
  return Math.max(5, Math.round(minutes * 60))
}

watch(
  () => props.modelValue,
  (value, previousValue) => {
    customSeconds.value = toRoundedSeconds(value)
    if (value === previousValue) {
      return
    }

    selectedOption.value = isPresetValue(value) ? Number(value) : CUSTOM_OPTION
  },
  { immediate: true }
)

function handleOptionChange(value: number | string) {
  if (value === CUSTOM_OPTION) {
    selectedOption.value = CUSTOM_OPTION
    customSeconds.value = toRoundedSeconds(props.modelValue)
    return
  }

  const nextValue = Number(value)
  selectedOption.value = nextValue
  emit('update:modelValue', nextValue)
}

function handleCustomSecondsChange(value: number | string | undefined) {
  const nextSeconds = Math.max(5, Math.round(Number(value) || customSeconds.value || 60))
  customSeconds.value = nextSeconds
  emit('update:modelValue', Number((nextSeconds / 60).toFixed(4)))
}
</script>

<style scoped>
.target-duration-field {
  display: grid;
  gap: 10px;
}

.target-duration-field__custom {
  display: flex;
  align-items: center;
  gap: 10px;
}

.target-duration-field__custom :deep(.el-input-number) {
  width: 180px;
}

.target-duration-field__suffix {
  color: #64748b;
  font-size: 13px;
}

@media (max-width: 640px) {
  .target-duration-field__custom {
    align-items: stretch;
    flex-direction: column;
  }

  .target-duration-field__custom :deep(.el-input-number) {
    width: 100%;
  }
}
</style>